import { Inject, Injectable, Optional } from '@angular/core';
import {
  EMPTY,
  Observable,
  catchError,
  concat,
  filter,
  first,
  of,
  switchMap,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslationsRepository } from '../../core/domain/translation/repositories/translations.repository';
import { TranslationModel } from '../../core/domain/translation/models/translation.model';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { loadAction } from './store/translation.actions';
import { LanguageModel } from '@/core/domain/translation/models/language.model';
import { Location } from '@angular/common';
import { getLanguage } from '@/common/utils/strings/path.utils';
import { CdnService } from '@/common/services/cdn/cdn.service';
import { SsrService } from '@/common/services/ssr/ssr.service';
import { selectTranslation } from './store/translation.selector';
import { environment } from '@/env/environment';

export const translatePrefixes = {
  title: 'titles',
  route: 'routes',
};

@Injectable({
  providedIn: 'root',
})
export class TranslationsImplRepository extends TranslationsRepository {
  constructor(
    private readonly store: Store,
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly location: Location,
    private readonly ssr: SsrService,
    @Optional() @Inject('LANG') public lang: string,
  ) {
    super();
  }

  override getLanguague(): LanguageModel {
    if (this.ssr.isPlatformServer()) {
      return { language: this.lang as 'es' | 'en' };
    }

    const fullUrl = window.location.href.split('#')[0];
    const paths = fullUrl.split('/');
    let currentLanguage = environment.allowedLanguages[0];

    paths.forEach((element) => {
      environment.allowedLanguages.forEach((language) => {
        if (language === element) {
          currentLanguage = language;
        }
      });
    });

    return { language: currentLanguage as 'es' | 'en' };
  }

  override load(lang: string): Observable<TranslationModel> {
    return concat(
      this.store.select(selectTranslation).pipe(first()),
      this.http.get(CdnService.url(lang + '.json')),
    ).pipe(
      filter((response) => {
        return (
          Object.keys(response).length > 0 &&
          response.constructor === Object
        );
      }),
      switchMap((response: unknown) => {
        const translations =
          typeof response === 'string'
            ? JSON.parse(response)
            : response;

        this.translate.setTranslation(lang, translations);
        this.translate.use(lang);
        this.store.dispatch(loadAction({ value: translations }));

        return of(response as TranslationModel);
      }),
      catchError((error) => {
        console.error('Error loading translations:', error);
        return EMPTY;
      }),
    );
  }

  override translateRoutes(): Observable<string> {
    const currentLanguage = getLanguage();
    const translations = this.translate.translations[currentLanguage];

    if (!translations) {
      return of(this.location.path());
    }

    const routesTranslate = translations['routes'];
    if (!routesTranslate) {
      return of(this.location.path());
    }

    const paths = this.location.path().replace(/^\/+/, '').split('/');
    const realPaths: string[] = [];

    paths.forEach((path) => {
      const key = Object.keys(routesTranslate).find(
        (key) => routesTranslate[key] === path,
      );
      realPaths.push(typeof key === 'string' ? key : path);
    });

    return of(realPaths.join('/'));
  }
}
